/**
 * LocationsSettings — Manage locations and storage areas (Settings > Locations tab).
 * Contains: locations list, add/edit/delete location dialogs, storage areas list,
 * add/edit/delete storage area dialogs.
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Warehouse, Stack, Plus, Check, Trash, Pencil } from "@phosphor-icons/react";

export function LocationsSettings({
  locations, storageAreas,
  selectedLocation, setSelectedLocation,
  locationDialogOpen, setLocationDialogOpen,
  newLocation, setNewLocation, onAddLocation, onDeleteLocation,
  areaDialogOpen, setAreaDialogOpen,
  newArea, setNewArea, onAddArea, onDeleteArea,
  editLocationOpen, setEditLocationOpen,
  editingLocation, setEditingLocation, onEditLocation, onSaveLocation,
  editAreaOpen, setEditAreaOpen,
  editingArea, setEditingArea, onEditArea, onSaveArea
}) {
  return (
    <>
      {/* Locations List */}
      <Card className="border rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
            <Warehouse size={20} weight="duotone" className="text-primary" />
            LOCATIONS
          </CardTitle>
          <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-primary" data-testid="add-location-btn">
                <Plus size={16} className="mr-2" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading tracking-wide">ADD LOCATION</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Location Name</Label>
                  <Input
                    data-testid="new-location-name"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Downtown Kitchen"
                  />
                </div>
                <div>
                  <Label>Address (Optional)</Label>
                  <Input
                    data-testid="new-location-address"
                    value={newLocation.address}
                    onChange={(e) => setNewLocation(p => ({ ...p, address: e.target.value }))}
                    placeholder="e.g., 123 Main St"
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setLocationDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={onAddLocation} className="bg-primary" data-testid="save-location-btn">
                  <Check size={16} className="mr-2" />
                  Add Location
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {locations.length > 0 ? (
            <div className="space-y-2">
              {locations.map(loc => (
                <div
                  key={loc.location_id}
                  data-testid={`location-item-${loc.location_id}`}
                  className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                    selectedLocation === loc.location_id ? "bg-primary/10 border border-primary" : "bg-muted/30 hover:bg-muted/50"
                  }`}
                  onClick={() => setSelectedLocation(loc.location_id)}
                >
                  <div>
                    <p className="font-medium">{loc.name}</p>
                    {loc.address && <p className="text-sm text-muted-foreground">{loc.address}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); onEditLocation(loc); }}
                      data-testid={`edit-location-${loc.location_id}`}
                    >
                      <Pencil size={16} className="text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); onDeleteLocation(loc.location_id); }}
                      data-testid={`delete-location-${loc.location_id}`}
                    >
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No locations configured. Add your first location to get started.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Storage Areas */}
      {selectedLocation && (
        <Card className="border rounded-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
              <Stack size={20} weight="duotone" className="text-secondary" />
              STORAGE AREAS
              <Badge variant="outline" className="ml-2">
                {locations.find(l => l.location_id === selectedLocation)?.name}
              </Badge>
            </CardTitle>
            <Dialog open={areaDialogOpen} onOpenChange={setAreaDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="add-area-btn">
                  <Plus size={16} className="mr-2" />
                  Add Area
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading tracking-wide">ADD STORAGE AREA</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>Area Name</Label>
                    <Input
                      data-testid="new-area-name"
                      value={newArea.name}
                      onChange={(e) => setNewArea(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., Walk-In Cooler"
                    />
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button variant="outline" onClick={() => setAreaDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={onAddArea} className="bg-primary" data-testid="save-area-btn">
                    <Check size={16} className="mr-2" />
                    Add Area
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {storageAreas.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {storageAreas.map(area => (
                  <div
                    key={area.area_id}
                    data-testid={`area-item-${area.area_id}`}
                    className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                  >
                    <span className="font-medium">{area.name}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditArea(area)}
                        data-testid={`edit-area-${area.area_id}`}
                      >
                        <Pencil size={16} className="text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteArea(area.area_id)}
                        data-testid={`delete-area-${area.area_id}`}
                      >
                        <Trash size={16} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No storage areas for this location. Add areas like "Freezer", "Walk-In", "Dry Storage".
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Location Dialog */}
      <Dialog open={editLocationOpen} onOpenChange={setEditLocationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">EDIT LOCATION</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Location Name</Label>
              <Input
                data-testid="edit-location-name"
                value={editingLocation?.name || ""}
                onChange={(e) => setEditingLocation(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Downtown Kitchen"
              />
            </div>
            <div>
              <Label>Address (Optional)</Label>
              <Input
                data-testid="edit-location-address"
                value={editingLocation?.address || ""}
                onChange={(e) => setEditingLocation(p => ({ ...p, address: e.target.value }))}
                placeholder="e.g., 123 Main St"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditLocationOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSaveLocation} className="bg-primary" data-testid="update-location-btn">
              <Check size={16} className="mr-2" />
              Update Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Storage Area Dialog */}
      <Dialog open={editAreaOpen} onOpenChange={setEditAreaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">EDIT STORAGE AREA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Area Name</Label>
              <Input
                data-testid="edit-area-name"
                value={editingArea?.name || ""}
                onChange={(e) => setEditingArea(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Walk-In Cooler"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditAreaOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSaveArea} className="bg-primary" data-testid="update-area-btn">
              <Check size={16} className="mr-2" />
              Update Area
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
